// Configs for each "For X" page. Keeps content modular & maintainable.
import { IMG } from "@/lib/images";

export const audienceConfigs = {
  "governments": {
    slug: "governments",
    eyebrow: "For Governments",
    title: "Hit your diversion, jobs and EPR targets with auditable infrastructure.",
    sub: "We help cities, states and ministries plan, finance and operate circular infrastructure that converts waste streams into local jobs, revenue and verified climate outcomes.",
    image: IMG.landscape.indianCitySkyline,
    pains: ["Landfill saturation", "Missing EPR enforcement", "Stranded waste budgets", "Political risk on incinerators"],
    offers: [
      ["Diversion roadmap", "City- or state-level digital twin of every waste stream."],
      ["Hub program", "Pre-engineered hubs financed via PPP / blended finance."],
      ["Verified outcomes", "Diversion, jobs, CO₂ — auditable for every quarter."],
      ["Procurement bundle", "Standard contracts + spec packs ready to issue."],
    ],
    metrics: [["75–95%", "Diversion potential"], ["6×", "Jobs vs. landfill"], ["3–5 yr", "Hub payback"]],
    cta: { label: "Plan a city pilot", to: "/contact" },
  },
  "landowners": {
    slug: "landowners",
    eyebrow: "For Landowners",
    title: "Turn 1–5 acres of idle land into circular yield.",
    sub: "Underused land + the right hub can produce stable lease income, revenue share and community impact — without farming risk or solar permit headaches.",
    image: IMG.community.farmlandAerial,
    pains: ["Idle land = zero yield", "Solar saturation in your district", "Selling land = one-time event"],
    offers: [
      ["Land match", "We match your land to feedstock, hub type and operator."],
      ["Lease + share", "Stable lease income plus optional revenue participation."],
      ["No operations", "A certified operator runs the hub — you remain the landlord."],
      ["Community model", "Local labour, training and microloans tied to the site."],
    ],
    metrics: [["$8–25k", "Lease per acre / year"], ["+5–12%", "Revenue share upside"], ["20+", "Jobs per hub"]],
    cta: { label: "List your land", to: "/marketplace" },
  },
  "waste-sources": {
    slug: "waste-sources",
    eyebrow: "For Waste / Feedstock Sources",
    title: "Find the value hiding in your residue.",
    sub: "If you generate food waste, plastics, textiles, biomass, C&D or e-waste — we tell you what it's worth, who buys it, and how to convert it into recurring revenue.",
    image: IMG.resource.plasticBales,
    pains: ["Disposal cost going up", "Brand auditors knocking", "Scope-3 reporting pressure"],
    offers: [
      ["Free value scan", "Run the Waste-to-Resource Engine in under a minute."],
      ["Offtake routes", "We surface verified buyers for each byproduct."],
      ["Onsite or shared hub", "Either we route to a nearby hub or build one with you."],
      ["EPR & credits", "Convert your output into traceable EPR / circular credits."],
    ],
    metrics: [["+$50–300", "Recoverable per tonne"], ["95%", "Diversion possible"], ["6 wks", "Time to first offtake"]],
    cta: { label: "Run the Engine", to: "/ai-tools/waste-to-resource" },
  },
  "recycling-tech": {
    slug: "recycling-tech",
    eyebrow: "For Recycling Technology Companies",
    title: "Skip selling equipment. Deploy through ready-built hubs.",
    sub: "Pre-financed sites, secured feedstock, verified offtake and standard interfaces — accelerate deployment without doing project finance every time.",
    image: IMG.industry.factoryLine,
    pains: ["Long sales cycles", "Project finance bottlenecks", "Feedstock uncertainty"],
    offers: [
      ["Hub slots", "Get matched to live sites needing your technology."],
      ["Standard specs", "Plug into Circularity Hub blueprints with known I/O."],
      ["Performance contracts", "Revenue share or tolling — your choice."],
      ["Verification", "Auditable yields tied directly to claims & credits."],
    ],
    metrics: [["50%", "Faster deployment"], ["$0", "BD cost per hub"], ["Tier-1", "Offtake partners"]],
    cta: { label: "Submit your tech", to: "/marketplace" },
  },
  "software-partners": {
    slug: "software-partners",
    eyebrow: "For Software Technology Partners",
    title: "Plug into the operating system of circularity.",
    sub: "If you build IoT, traceability, MRV, fintech, marketplace or AI software — Circularity Global is the integration layer between physical assets and capital markets.",
    image: IMG.data.serverRoom,
    pains: ["Fragmented circular data", "No standard schemas", "Every hub redefines tracking"],
    offers: [
      ["Open schemas", "Material, flow and DPP schemas to integrate against."],
      ["API access", "Hub, marketplace and credit APIs for your stack."],
      ["Co-deployment", "Bundle with our hub & investor pipeline."],
      ["Revenue share", "Tie your fees to verified hub performance."],
    ],
    metrics: [["50+", "Hub deployments to integrate"], ["1 API", "Per material class"], ["Open", "Standards-first"]],
    cta: { label: "Become an integration partner", to: "/contact" },
  },
  "offtake-buyers": {
    slug: "offtake-buyers",
    eyebrow: "For Offtake Buyers",
    title: "Secure verified circular supply, contractually.",
    sub: "Brands, manufacturers, utilities and traders can sign long-term offtake with hubs at fair prices — with traceability strong enough to survive any audit.",
    image: IMG.network.cargoShip,
    pains: ["Recycled supply unreliable", "Greenwashing scrutiny", "Margin volatility on virgin feedstock"],
    offers: [
      ["Long-term offtake", "5–10 year contracts at indexed prices."],
      ["Traceability", "Every batch tied to a DPP + audit trail."],
      ["Brand-grade quality", "Spec-controlled hubs with QA labs."],
      ["EPR partner", "Bundle offtake with EPR target delivery."],
    ],
    metrics: [["10 yr", "Contracts available"], ["100%", "Traceable to source"], ["≤ ±5%", "Spec deviation"]],
    cta: { label: "Browse offtake catalogue", to: "/marketplace" },
  },
  "investors": {
    slug: "investors",
    eyebrow: "For Investors",
    title: "Invest in circular infrastructure that generates recurring cash flows.",
    sub: "Investors are not buying 'waste'. They are buying infrastructure assets that monetise resource conversion, offtake sales, marketplace fees, financing, software, verification and data intelligence.",
    image: IMG.industry.portContainers,
    pains: ["Impact funds with weak unit economics", "Climate tech without contracts", "ESG without verification"],
    offers: [
      ["Hub SPVs", "Direct asset-level investment with contracted offtake."],
      ["Portfolio funds", "Diversified across hub archetypes & geographies."],
      ["Revenue participation", "Quarterly cash flows tied to verified outputs."],
      ["Future: tokenised stakes", "Fractional, secondary-market-ready on our roadmap."],
    ],
    metrics: [["14–26%", "IRR ranges by hub type"], ["3–5 yr", "Payback"], ["Verified", "Cash + impact"]],
    cta: { label: "Open the Investor Portal", to: "/portal" },
  },
  "fintech": {
    slug: "fintech",
    eyebrow: "For Fintech / Banks",
    title: "Finance the real economy of materials.",
    sub: "Working capital, equipment leasing, receivables, supply-chain finance — circular infrastructure produces well-defined, asset-backed flows that traditional finance can underwrite.",
    image: IMG.data.chartsDark,
    pains: ["Hard to underwrite recyclers", "Receivables data sparse", "No standardised collateral"],
    offers: [
      ["Asset-backed", "Hubs are physical, contracted, audited."],
      ["Data feed", "Real-time flow + invoice + receivable visibility."],
      ["Risk sharing", "Co-finance with concessional capital where relevant."],
      ["Embedded loans", "Microloans to Nodes inside the Foundation programme."],
    ],
    metrics: [["≤ 2%", "Default rate target"], ["90 d", "Receivable cycles"], ["Audited", "Underwriting data"]],
    cta: { label: "Co-design a product", to: "/contact" },
  },
  "corporates": {
    slug: "corporates",
    eyebrow: "For Corporates",
    title: "Convert ESG ambition into auditable infrastructure.",
    sub: "Hit EPR, Scope-3 and circularity targets with infrastructure that actually delivers — not consulting decks. Co-invest, anchor offtake, or sponsor a community hub.",
    image: IMG.industry.cleanFactory,
    pains: ["EPR liabilities rising", "Scope-3 unmoveable", "ESG audits getting tougher"],
    offers: [
      ["Anchor a hub", "Lock in long-term offtake of recycled materials."],
      ["Co-invest", "Take an equity stake; book impact alongside returns."],
      ["EPR delivery", "Contract verified diversion at scale."],
      ["Reporting", "Quarterly dashboards your auditors love."],
    ],
    metrics: [["100%", "Verified outputs"], ["Top quartile", "ESG signal strength"], ["Co-owned", "Community share"]],
    cta: { label: "Talk to our corporate team", to: "/contact" },
  },
};
